import { SelfserviceSelfServiceSubmission, SelfserviceSelfServiceSubmissionResponse } from '@libs/generated-types/selfservice';


//Map the form payload
type SelfServicePayload = SelfserviceSelfServiceSubmission['selfService'];
type SelfServiceKind = SelfServicePayload['selfServiceKind'];

type PayloadFor<K extends SelfServiceKind> = Extract<
    SelfServicePayload,
    { selfServiceKind: K }
>;

export const SELF_SERVICE_MAPPERS: {
    [K in SelfServiceKind]?: (formValue: any) => PayloadFor<K>;
} = {

    FIRST_EMPLOYMENT: (f) => ({
        selfServiceKind: 'FIRST_EMPLOYMENT',
        isBeforeJan2007: f.isBeforeJan2007,
    }),

    RETIREMENT_PLAN: (f) => ({
        selfServiceKind: 'RETIREMENT_PLAN',
        contributionsBefore1996: f.contributionsBefore1996,
    }),

    TFR_DECLARATION: (f) => {
        const primaryChoice = f.tfrPrimaryChoice
            ?? (f.firstTime
                ? 'firstTime'
                : f.alreadyInCompany
                    ? 'alreadyInCompany'
                    : f.ransom
                        ? 'ransom'
                        : f.notRansom
                            ? 'notRansom'
                            : undefined);
        return {
            selfServiceKind: 'TFR_DECLARATION',
            firstTime: primaryChoice === 'firstTime',
            alreadyInCompany: primaryChoice === 'alreadyInCompany',
            ransom: primaryChoice === 'ransom',
            notRansom: primaryChoice === 'notRansom',
            inCompany: primaryChoice === 'firstTime' ? f.inCompany === true : undefined,
        };
    },

    PERSONAL_CONTACTS_DETAILS: (f) => ({
        selfServiceKind: 'PERSONAL_CONTACTS_DETAILS',
        email: f.email,
        mobilePhone: f.mobilePhone,
        landlinePhone: f.landlinePhone ?? undefined,
        notesAcknowledged: f.notesAcknowledged,
    }),

    PREVIOUS_WORK_ACTIVITIES: (f) => ({
        selfServiceKind: 'PREVIOUS_WORK_ACTIVITIES',
        noSubordinateEmployment: f.noSubordinateEmployment,
        noSubordinateEmploymentUnder6Months: f.noSubordinateEmploymentUnder6Months,
        selfEmployment: f.selfEmployment,
        coordinatedCollaborationContract: f.coordinatedCollaborationContract,
        hasCompletedSecondarySchool: f.hasCompletedSecondarySchool,
    }),


    EMPLOYMENT_HISTORY: (f) => ({
        selfServiceKind: 'EMPLOYMENT_HISTORY',
        employmentHistoryDetails:
            f.kind === 'NO_PREVIOUS_EMPLOYMENT'
                ? { hasPreviousEmployment: 'NO_PREVIOUS_EMPLOYMENT' }
                : {
                    hasPreviousEmployment: 'WITH_PREVIOUS_EMPLOYMENT',
                    sickDaysCount: f.sickDaysCount,
                    taxableSocialSecurityAmount: f.taxableSocialSecurityAmount,
                    fringeBenefitsAmount: f.fringeBenefitsAmount,
                    hasSeverancePay: f.hasSeverancePay,
                },
    }),


    ADDRESS_DECLARATION: (f) => ({
        selfServiceKind: 'ADDRESS_DECLARATION',
        addressDetails:
            f.kind === 'FOREIGN'
                ? { addressKind: 'FOREIGN' }
                : {
                    addressKind: 'ITALIAN',
                    region: f.stateOrRegion,
                    province: f.provinceOrCounty,
                    city: f.city,
                },
    }),


    PAYMENT_DETAILS_CHANGE: (f) => ({
        selfServiceKind: 'PAYMENT_DETAILS_CHANGE',
        paymentDetails:
            f.kind === 'POSTAL'
                ? {
                    paymentMethodKind: 'POSTAL',
                    notesAcknowledged: true,
                    accountHolder: f.accountHolder,
                }
                : {
                    paymentMethodKind: 'BANK_TRANSFER',
                    notesAcknowledged: true,
                    accountHolder: f.accountHolder,
                    iban: f.iban,
                    accountCoHolder: f.accountCoHolder?.firstName
                        ? f.accountCoHolder
                        : undefined,
                },
    }),

    DETASSAZIONE_COMMERCIO: (f) => {
        const identityCardAttachments = Array.isArray(f.identityCardAttachments)
            ? f.identityCardAttachments.map((attachment: string | { key: string }) =>
                typeof attachment === 'string' ? attachment : attachment.key)
            : [];

        return {
            selfServiceKind: 'DETASSAZIONE_COMMERCIO',
            incomeAcceptance: f.incomeAcceptance,
            income: f.income ?? 0,
            alreadyTaxedSubstituteTaxAcceptance: f.alreadyTaxedSubstituteTaxAcceptance,
            alreadyTaxedSubstituteTaxAmount: f.alreadyTaxedSubstituteTaxAmount ?? 0,
            reliefAcceptance: f.reliefAcceptance,
            acceptance: f.acceptance,
            nightOrHolidayWorkAcceptance: f.nightOrHolidayWorkAcceptance,
            identityCardAttachments,
        };
    },

    DETASSAZIONE_TURISMO: (f) => {
        const identityCardAttachments = Array.isArray(f.identityCardAttachments)
            ? f.identityCardAttachments.map((attachment: string | { key: string }) =>
                typeof attachment === 'string' ? attachment : attachment.key)
            : [];

        return {
            selfServiceKind: 'DETASSAZIONE_TURISMO',
            incomeAcceptance: f.incomeAcceptance,
            income: f.income ?? 0,
            reliefAcceptance: f.reliefAcceptance,
            acceptance: f.acceptance,
            identityCardAttachments,
        };
    },
};


//Reverse map the payload for display filled forms

type SelfServiceResponsePayload = SelfserviceSelfServiceSubmissionResponse['selfService'];

type ResponseFor<K extends SelfServiceKind> = Extract<
    SelfServiceResponsePayload,
    { selfServiceKind: K }
>;

export const SELF_SERVICE_REVERSE_MAPPERS: {
    [K in SelfServiceKind]?: (payload: ResponseFor<K>) => Record<string, unknown>;
} = {


    FIRST_EMPLOYMENT: (p) => ({
        isBeforeJan2007: p.isBeforeJan2007,
    }),

    RETIREMENT_PLAN: (p) => ({
        contributionsBefore1996: p.contributionsBefore1996,
    }),

    TFR_DECLARATION: (p) => ({
        firstTime: p.firstTime,
        alreadyInCompany: p.alreadyInCompany,
        ransom: p.ransom,
        notRansom: p.notRansom,
        inCompany: p.firstTime ? p.inCompany ?? false : undefined,
        tfrPrimaryChoice: p.firstTime
            ? 'firstTime'
            : p.alreadyInCompany
                ? 'alreadyInCompany'
                : p.ransom
                    ? 'ransom'
                    : p.notRansom
                        ? 'notRansom'
                        : null,
    }),

    PERSONAL_CONTACTS_DETAILS: (p) => ({
        email: p.email,
        mobilePhone: p.mobilePhone,
        landlinePhone: p.landlinePhone,
        notesAcknowledged: true,
    }),

    PREVIOUS_WORK_ACTIVITIES: (p) => ({
        noSubordinateEmployment: p.noSubordinateEmployment,
        noSubordinateEmploymentUnder6Months: p.noSubordinateEmploymentUnder6Months,
        selfEmployment: p.selfEmployment,
        coordinatedCollaborationContract: p.coordinatedCollaborationContract,
        hasCompletedSecondarySchool: p.hasCompletedSecondarySchool,
    }),


    EMPLOYMENT_HISTORY: (p) => {
        const d = p.employmentHistoryDetails;

        if (!d) {
            return {};
        }

        if (d.hasPreviousEmployment === 'NO_PREVIOUS_EMPLOYMENT') {
            return { kind: 'NO_PREVIOUS_EMPLOYMENT' };
        }

        return {
            kind: 'WITH_PREVIOUS_EMPLOYMENT',
            sickDaysCount: d.sickDaysCount,
            taxableSocialSecurityAmount: d.taxableSocialSecurityAmount,
            fringeBenefitsAmount: d.fringeBenefitsAmount,
            hasSeverancePay: d.hasSeverancePay,
        };
    },

    ADDRESS_DECLARATION: (p) => {
        const d = p.addressDetails;

        if (!d) {
            return {};
        }

        if (d.addressKind === 'FOREIGN') {
            return { kind: 'FOREIGN' };
        }

        return {
            kind: 'ITALIAN',
            stateOrRegion: d.region,
            provinceOrCounty: d.province,
            city: d.city,
        };
    },

    PAYMENT_DETAILS_CHANGE: (p) => {
        const d = p.paymentDetails;

        if (!d) {
            return {};
        }

        const base = {
            kind: d.paymentMethodKind,
            notesAcknowledged: true,
            accountHolder: d.accountHolder,
        };

        if (d.paymentMethodKind === 'POSTAL') {
            return base;
        }

        return {
            ...base,
            iban: d.iban,
            accountCoHolder: d.accountCoHolder ?? { firstName: null, lastName: null },
        };
    },

    DETASSAZIONE_COMMERCIO: (p) => ({
        incomeAcceptance: p.incomeAcceptance,
        income: p.income,
        alreadyTaxedSubstituteTaxAcceptance: p.alreadyTaxedSubstituteTaxAcceptance,
        alreadyTaxedSubstituteTaxAmount: p.alreadyTaxedSubstituteTaxAmount ?? null,
        reliefAcceptance: p.reliefAcceptance,
        acceptance: p.acceptance,
        nightOrHolidayWorkAcceptance: p.nightOrHolidayWorkAcceptance,
        identityCardAttachments: p.identityCardAttachments ?? [],
    }),

    DETASSAZIONE_TURISMO: (p) => ({
        incomeAcceptance: p.incomeAcceptance,
        income: p.income,
        reliefAcceptance: p.reliefAcceptance,
        acceptance: p.acceptance,
        identityCardAttachments: p.identityCardAttachments ?? [],
    }),
};